import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, 
  Download, 
  Calendar, 
  TrendingUp, 
  DollarSign,
  Users,
  Package,
  Filter,
  Loader,
  UserCog,
  BarChart3,
  Search,
  Printer,
  ChevronDown,
  ChevronUp,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Relatorios = () => {
  const [tipoRelatorio, setTipoRelatorio] = useState('geral');
  const [periodo, setPeriodo] = useState({
    dataInicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0]
  });
  const [filtros, setFiltros] = useState({
    vendedor_id: '',
    cliente_id: '',
  });
  const [vendedores, setVendedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    produtos: true,
    clientes: true,
    vendedores: true,
    romaneios: false
  });
  const [ordenacao, setOrdenacao] = useState({ campo: 'nome', direcao: 'asc' });

  useEffect(() => {
    fetchVendedores();
    fetchClientes();
  }, []);

  const fetchVendedores = async () => {
    try {
      const response = await axios.get(`${API}/sellers`);
      setVendedores(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar vendedores:', error);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClientes(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const handleGerarRelatorio = async () => {
    if (!periodo.dataInicio || !periodo.dataFim) {
      toast.error('Selecione o período do relatório');
      return;
    }

    setLoading(true);
    try {
      const params = {
        data_inicio: periodo.dataInicio,
        data_fim: periodo.dataFim,
        tipo: tipoRelatorio,
        ...filtros
      };

      const response = await axios.get(`${API}/relatorios/gerar`, { params });
      setDadosRelatorio(response.data);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBaixarPDF = async () => {
    if (!dadosRelatorio) {
      toast.error('Gere o relatório primeiro');
      return;
    }

    setGeneratingPDF(true);
    try {
      const params = {
        data_inicio: periodo.dataInicio,
        data_fim: periodo.dataFim,
        tipo: tipoRelatorio,
        ...filtros
      };

      const url = `${API}/relatorios/pdf?${new URLSearchParams(params).toString()}`;
      window.open(url, '_blank');
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
      console.error(error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const formatarNumero = (valor) => {
    if (valor === undefined || valor === null) return '0';
    return valor % 1 === 0 ? Math.floor(valor).toLocaleString('pt-BR') : valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const tiposRelatorio = [
    { id: 'geral', nome: 'Relatório Geral', icon: BarChart3, descricao: 'Visão completa de vendas, clientes e produtos' },
    { id: 'produtos', nome: 'Por Produtos', icon: Package, descricao: 'Lista completa de produtos vendidos (A-Z)' },
    { id: 'clientes', nome: 'Por Clientes', icon: Users, descricao: 'Lista completa de clientes (A-Z)' },
    { id: 'vendedores', nome: 'Por Vendedores', icon: UserCog, descricao: 'Desempenho detalhado por vendedor' },
    { id: 'comissoes', nome: 'Comissões', icon: DollarSign, descricao: 'Comissões a pagar por vendedor' },
    { id: 'vendas', nome: 'Romaneios', icon: TrendingUp, descricao: 'Lista de todos os romaneios do período' }
  ];

  // Componente de seção colapsável
  const SectionHeader = ({ title, icon: Icon, section, count, extraInfo }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 rounded-t-lg border border-border hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="text-left">
          <h4 className="font-semibold text-foreground">{title}</h4>
          {extraInfo && <p className="text-xs text-muted-foreground">{extraInfo}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {count !== undefined && (
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-bold">
            {count} itens
          </span>
        )}
        {expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </div>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-heading font-bold text-foreground">Relatórios</h2>
            <p className="text-muted-foreground mt-1">Relatórios profissionais e detalhados</p>
          </div>
        </div>
      </div>

      {/* Tipo de Relatório */}
      <div className="stat-card">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Tipo de Relatório</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiposRelatorio.map((tipo) => {
            const Icon = tipo.icon;
            const isActive = tipoRelatorio === tipo.id;
            return (
              <button
                key={tipo.id}
                onClick={() => setTipoRelatorio(tipo.id)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  isActive
                    ? 'border-primary bg-primary/10 shadow-md'
                    : 'border-border hover:border-primary/50 bg-background'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isActive ? 'bg-primary text-white' : 'bg-muted'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${isActive ? 'text-primary' : 'text-foreground'}`}>
                      {tipo.nome}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">{tipo.descricao}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filtros */}
      <div className="stat-card">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Período e Filtros</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Data Início</label>
            <input
              type="date"
              value={periodo.dataInicio}
              onChange={(e) => setPeriodo({...periodo, dataInicio: e.target.value})}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Data Fim</label>
            <input
              type="date"
              value={periodo.dataFim}
              onChange={(e) => setPeriodo({...periodo, dataFim: e.target.value})}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Vendedor (Opcional)</label>
            <select
              value={filtros.vendedor_id}
              onChange={(e) => setFiltros({...filtros, vendedor_id: e.target.value})}
              className="input-field"
            >
              <option value="">Todos os vendedores</option>
              {vendedores.sort((a, b) => a.nome?.localeCompare(b.nome)).map((v) => (
                <option key={v.id} value={v.id}>{v.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Cliente (Opcional)</label>
            <select
              value={filtros.cliente_id}
              onChange={(e) => setFiltros({...filtros, cliente_id: e.target.value})}
              className="input-field"
            >
              <option value="">Todos os clientes</option>
              {clientes.sort((a, b) => a.nome?.localeCompare(b.nome)).map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleGerarRelatorio}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Gerar Relatório
              </>
            )}
          </button>

          {dadosRelatorio && (
            <button
              onClick={handleBaixarPDF}
              disabled={generatingPDF}
              className="btn-secondary flex items-center gap-2"
            >
              {generatingPDF ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4" />
                  Imprimir / PDF
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Resultados */}
      {dadosRelatorio && (
        <div className="space-y-6">
          {/* Header do Relatório */}
          <div className="stat-card bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold text-xl text-foreground">
                  {tiposRelatorio.find(t => t.id === tipoRelatorio)?.nome}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Período: {new Date(periodo.dataInicio).toLocaleDateString('pt-BR')} a {new Date(periodo.dataFim).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Gerado em</p>
                <p className="text-sm font-medium">{new Date().toLocaleString('pt-BR')}</p>
              </div>
            </div>
          
            {/* Stats Resumo */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-muted-foreground font-medium">Total Vendas</span>
                </div>
                <p className="text-xl font-bold text-green-600">
                  {formatarMoeda(dadosRelatorio.total_vendas)}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-muted-foreground font-medium">Total Pedidos</span>
                </div>
                <p className="text-xl font-bold text-blue-600">
                  {dadosRelatorio.total_pedidos || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-muted-foreground font-medium">Ticket Médio</span>
                </div>
                <p className="text-xl font-bold text-purple-600">
                  {formatarMoeda(dadosRelatorio.ticket_medio)}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-orange-600" />
                  <span className="text-xs text-muted-foreground font-medium">Clientes Ativos</span>
                </div>
                <p className="text-xl font-bold text-orange-600">
                  {dadosRelatorio.clientes_ativos || 0}
                </p>
              </div>

              {dadosRelatorio.total_comissoes > 0 && (
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-muted-foreground font-medium">Total Comissões</span>
                  </div>
                  <p className="text-xl font-bold text-emerald-600">
                    {formatarMoeda(dadosRelatorio.total_comissoes)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ========== PRODUTOS ========== */}
          {(dadosRelatorio.detalhes?.todos_produtos || dadosRelatorio.detalhes?.top_produtos) && (
            <div className="stat-card p-0 overflow-hidden">
              <SectionHeader 
                title="Produtos Vendidos" 
                icon={Package} 
                section="produtos"
                count={dadosRelatorio.detalhes?.todos_produtos?.length || dadosRelatorio.detalhes?.top_produtos?.length}
                extraInfo={dadosRelatorio.detalhes?.total_itens_vendidos ? `${formatarNumero(dadosRelatorio.detalhes.total_itens_vendidos)} unidades vendidas` : null}
              />
              
              {expandedSections.produtos && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">#</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                          <div className="flex items-center gap-1 cursor-pointer hover:text-primary">
                            Produto <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Quantidade</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Nº Vendas</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Preço Médio</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Valor Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(dadosRelatorio.detalhes?.todos_produtos || dadosRelatorio.detalhes?.top_produtos || []).map((produto, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          <td className="px-4 py-3 text-sm text-muted-foreground">{idx + 1}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-foreground">{produto.nome}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium">{formatarNumero(produto.quantidade)}</td>
                          <td className="px-4 py-3 text-sm text-right text-muted-foreground">{produto.num_vendas || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-muted-foreground">{formatarMoeda(produto.preco_medio)}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-green-600">{formatarMoeda(produto.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 dark:bg-slate-700 font-bold">
                      <tr>
                        <td colSpan="2" className="px-4 py-3 text-sm">TOTAL</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {formatarNumero((dadosRelatorio.detalhes?.todos_produtos || dadosRelatorio.detalhes?.top_produtos || []).reduce((acc, p) => acc + (p.quantidade || 0), 0))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {(dadosRelatorio.detalhes?.todos_produtos || dadosRelatorio.detalhes?.top_produtos || []).reduce((acc, p) => acc + (p.num_vendas || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">-</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">
                          {formatarMoeda((dadosRelatorio.detalhes?.todos_produtos || dadosRelatorio.detalhes?.top_produtos || []).reduce((acc, p) => acc + (p.valor_total || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== CLIENTES ========== */}
          {(dadosRelatorio.detalhes?.todos_clientes || dadosRelatorio.detalhes?.top_clientes) && (
            <div className="stat-card p-0 overflow-hidden">
              <SectionHeader 
                title="Clientes" 
                icon={Users} 
                section="clientes"
                count={dadosRelatorio.detalhes?.todos_clientes?.length || dadosRelatorio.detalhes?.top_clientes?.length}
              />
              
              {expandedSections.clientes && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">#</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Cliente</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Pedidos</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Ticket Médio</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Produtos Dif.</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Total Compras</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(dadosRelatorio.detalhes?.todos_clientes || dadosRelatorio.detalhes?.top_clientes || []).map((cliente, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          <td className="px-4 py-3 text-sm text-muted-foreground">{idx + 1}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-foreground">{cliente.nome}</td>
                          <td className="px-4 py-3 text-sm text-right">{cliente.quantidade_pedidos}</td>
                          <td className="px-4 py-3 text-sm text-right text-muted-foreground">{formatarMoeda(cliente.ticket_medio)}</td>
                          <td className="px-4 py-3 text-sm text-right text-muted-foreground">{cliente.num_produtos_diferentes || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-green-600">{formatarMoeda(cliente.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 dark:bg-slate-700 font-bold">
                      <tr>
                        <td colSpan="2" className="px-4 py-3 text-sm">TOTAL</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {(dadosRelatorio.detalhes?.todos_clientes || dadosRelatorio.detalhes?.top_clientes || []).reduce((acc, c) => acc + (c.quantidade_pedidos || 0), 0)}
                        </td>
                        <td colSpan="2" className="px-4 py-3 text-sm text-right">-</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">
                          {formatarMoeda((dadosRelatorio.detalhes?.todos_clientes || dadosRelatorio.detalhes?.top_clientes || []).reduce((acc, c) => acc + (c.valor_total || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== VENDEDORES ========== */}
          {(dadosRelatorio.detalhes?.todos_vendedores || dadosRelatorio.detalhes?.vendedores) && (
            <div className="stat-card p-0 overflow-hidden">
              <SectionHeader 
                title="Vendedores" 
                icon={UserCog} 
                section="vendedores"
                count={dadosRelatorio.detalhes?.todos_vendedores?.length || dadosRelatorio.detalhes?.vendedores?.length}
                extraInfo={dadosRelatorio.total_comissoes > 0 ? `Total Comissões: ${formatarMoeda(dadosRelatorio.total_comissoes)}` : null}
              />
              
              {expandedSections.vendedores && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">#</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Vendedor</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Pedidos</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Clientes</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Total Vendas</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">% Comissão</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Comissão</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(dadosRelatorio.detalhes?.todos_vendedores || dadosRelatorio.detalhes?.vendedores || []).map((vendedor, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          <td className="px-4 py-3 text-sm text-muted-foreground">{idx + 1}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-foreground">{vendedor.nome}</td>
                          <td className="px-4 py-3 text-sm text-right">{vendedor.quantidade_pedidos}</td>
                          <td className="px-4 py-3 text-sm text-right text-muted-foreground">{vendedor.num_clientes || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-green-600">{formatarMoeda(vendedor.valor_total)}</td>
                          <td className="px-4 py-3 text-sm text-right text-muted-foreground">{vendedor.percentual_comissao || 0}%</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-blue-600">{formatarMoeda(vendedor.comissao)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 dark:bg-slate-700 font-bold">
                      <tr>
                        <td colSpan="2" className="px-4 py-3 text-sm">TOTAL</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {(dadosRelatorio.detalhes?.todos_vendedores || dadosRelatorio.detalhes?.vendedores || []).reduce((acc, v) => acc + (v.quantidade_pedidos || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">-</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">
                          {formatarMoeda((dadosRelatorio.detalhes?.todos_vendedores || dadosRelatorio.detalhes?.vendedores || []).reduce((acc, v) => acc + (v.valor_total || 0), 0))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">-</td>
                        <td className="px-4 py-3 text-sm text-right text-blue-600">
                          {formatarMoeda((dadosRelatorio.detalhes?.todos_vendedores || dadosRelatorio.detalhes?.vendedores || []).reduce((acc, v) => acc + (v.comissao || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== ROMANEIOS ========== */}
          {dadosRelatorio.detalhes?.romaneios && dadosRelatorio.detalhes.romaneios.length > 0 && (
            <div className="stat-card p-0 overflow-hidden">
              <SectionHeader 
                title="Romaneios do Período" 
                icon={FileText} 
                section="romaneios"
                count={dadosRelatorio.detalhes.romaneios.length}
              />
              
              {expandedSections.romaneios && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Nº Pedido</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Vendedor</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Itens</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Pagamento</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {dadosRelatorio.detalhes.romaneios.map((rom, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          <td className="px-4 py-3 text-sm font-bold text-primary">{rom.numero_pedido}</td>
                          <td className="px-4 py-3 text-sm">{rom.data ? new Date(rom.data).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="px-4 py-3 text-sm font-medium">{rom.cliente}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{rom.vendedor || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right">{rom.num_itens}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              rom.forma_pagamento?.toLowerCase().includes('pago') ? 'bg-green-100 text-green-700' :
                              rom.forma_pagamento?.toLowerCase().includes('prazo') ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {rom.forma_pagamento || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-green-600">{formatarMoeda(rom.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
